import { Scale, Receipt, Lock, FileText, Calendar, Hourglass, CircleDollarSign, ShieldAlert } from "lucide-react";
import { useState } from "react";

/**
 * Pagina "Fedina Penale" — VITA DA CRIMINALE.
 * Mostra MULTE (amministrative, plugin custom) e ARRESTI (plugin custom).
 * Se i dati sono vuoti, mostra lo stato vuoto "Non ne hai".
 */
function MultaCard({ multa }) {
  const paid = (multa.stato || "").toLowerCase() === "pagata";
  return (
    <div className="sanction-card p-5 flex flex-col gap-4" data-testid="multa-card">
      <div className="flex items-start justify-between gap-3">
        <span className="badge-amm">Amministrativa</span>
        <span className="inline-flex items-center gap-1.5 text-xs text-[hsl(var(--text-soft))] bg-[hsl(var(--bg-3))] border border-[hsl(var(--border-soft))] rounded-md px-2 py-1">
          <Calendar className="w-3 h-3" /> {multa.data || "—"}
        </span>
      </div>

      <div className="text-xl font-semibold text-white">{multa.articolo || "—"}</div>

      <div className="grid grid-cols-2 gap-2.5">
        <div className="sub-tile">
          <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-mute))]">Scadenza</div>
          <div className="text-sm font-medium text-white mt-1 inline-flex items-center gap-1.5">
            <Hourglass className="w-3.5 h-3.5 text-[hsl(var(--brand-400))]" /> {multa.scadenza || "—"}
          </div>
        </div>
        <div className="sub-tile">
          <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-mute))]">Importo</div>
          <div className="text-sm font-medium text-white mt-1 inline-flex items-center gap-1.5">
            <CircleDollarSign className="w-3.5 h-3.5 text-[hsl(var(--brand-400))]" />
            {multa.importo != null ? `${Number(multa.importo).toLocaleString("it-IT", { minimumFractionDigits: 2 })} €` : "—"}
          </div>
        </div>
      </div>

      <div className="flex items-end justify-between mt-1">
        <span className="inline-flex w-9 h-9 rounded-md items-center justify-center bg-[hsl(var(--brand-500)/0.18)] text-[hsl(var(--brand-300))] border border-[hsl(var(--brand-500)/0.35)]">
          <Receipt className="w-4 h-4" />
        </span>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-mute))]">Stato</div>
          <div className={`text-sm font-bold mt-0.5 ${paid ? "text-emerald-400" : "text-amber-300"}`}>
            {paid ? "✓ Pagata" : (multa.stato || "Da pagare").toUpperCase()}
          </div>
        </div>
      </div>
    </div>
  );
}

function ArrestoCard({ arresto }) {
  return (
    <div className="sanction-card p-5 flex flex-col gap-4" data-testid="arresto-card">
      <div className="flex items-start justify-between gap-3">
        <span className="badge-pen">Penale</span>
        <span className="inline-flex items-center gap-1.5 text-xs text-[hsl(var(--text-soft))] bg-[hsl(var(--bg-3))] border border-[hsl(var(--border-soft))] rounded-md px-2 py-1">
          <Calendar className="w-3 h-3" /> {arresto.data || "—"}
        </span>
      </div>

      <div className="text-xl font-semibold text-white">{arresto.reato || "—"}</div>

      <div className="grid grid-cols-2 gap-2.5">
        <div className="sub-tile">
          <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-mute))]">Durata</div>
          <div className="text-sm font-medium text-white mt-1 inline-flex items-center gap-1.5">
            <Hourglass className="w-3.5 h-3.5 text-red-300" /> {arresto.durata || "—"}
          </div>
        </div>
        <div className="sub-tile">
          <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-mute))]">Agente</div>
          <div className="text-sm font-medium text-white mt-1 truncate">
            {arresto.agente || "—"}
          </div>
        </div>
      </div>

      <div className="flex items-end justify-between mt-1">
        <span className="inline-flex w-9 h-9 rounded-md items-center justify-center bg-[hsl(var(--danger)/0.18)] text-red-300 border border-[hsl(var(--danger)/0.35)]">
          <Lock className="w-4 h-4" />
        </span>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-mute))]">Stato</div>
          <div className="text-sm font-bold text-red-300 mt-0.5">
            {(arresto.stato || "Scontato").toUpperCase()}
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyCriminal({ icon: Icon, title, text }) {
  return (
    <div className="card-surface flex flex-col items-center justify-center text-center py-16 px-6" data-testid="empty-criminale">
      <Icon className="w-12 h-12 mb-4 text-[hsl(var(--text-mute))]" strokeWidth={1.4} />
      <p className="text-xl font-semibold text-white">{title}</p>
      <p className="text-sm mt-2 text-[hsl(var(--text-soft))] max-w-md">{text}</p>
    </div>
  );
}

export default function FedinaPenale({ stats }) {
  const [filter, setFilter] = useState("tutto"); // tutto | sanzioni | arresti
  const multe = stats?.multe || [];
  const arresti = stats?.arresti || [];
  const totalCount = multe.length + arresti.length;

  const showMulte = filter === "tutto" || filter === "sanzioni";
  const showArresti = filter === "tutto" || filter === "arresti";

  return (
    <section className="mt-20 crime-bg rounded-3xl p-6 sm:p-10" data-testid="section-fedina">
      {/* Heading */}
      <div className="text-center fade-up">
        <h2 className="font-display title-stroke text-5xl sm:text-7xl tracking-wider leading-none">
          Vita da criminale
        </h2>
        <p className="mt-4 text-[10px] sm:text-xs uppercase tracking-[0.45em] text-[hsl(var(--text-mute))]">
          Rapporti con la legge e affiliazioni criminali
        </p>
        <div className="crime-divider mt-7" />
      </div>

      {/* Header bar */}
      <div className="mt-12 card-surface p-5 sm:p-6 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between" data-testid="provvedimenti-head">
        <div className="flex items-center gap-4">
          <span className="inline-flex w-12 h-12 rounded-xl items-center justify-center bg-[hsl(var(--bg-3))] border border-[hsl(var(--border-soft))]">
            <Scale className="w-6 h-6 text-[hsl(var(--text-soft))]" />
          </span>
          <div>
            <h3 className="text-xl font-semibold text-white">Provvedimenti giudiziari</h3>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--text-mute))] mt-1.5">
              Panoramica sanzioni e arresti
            </p>
          </div>
        </div>

        <div className="seg-wrap" data-testid="fedina-filter">
          <button
            data-testid="filter-tutto"
            onClick={() => setFilter("tutto")}
            className={`seg-pill ${filter === "tutto" ? "seg-pill-active" : ""}`}
          >
            Tutto
          </button>
          <button
            data-testid="filter-sanzioni"
            onClick={() => setFilter("sanzioni")}
            className={`seg-pill ${filter === "sanzioni" ? "seg-pill-active" : ""}`}
          >
            <Receipt className="w-3.5 h-3.5" /> Sanzioni
          </button>
          <button
            data-testid="filter-arresti"
            onClick={() => setFilter("arresti")}
            className={`seg-pill ${filter === "arresti" ? "seg-pill-active" : ""}`}
          >
            <Lock className="w-3.5 h-3.5" /> Arresti
          </button>
        </div>
      </div>

      {/* Lists */}
      {totalCount === 0 ? (
        <div className="mt-8">
          <EmptyCriminal
            icon={ShieldAlert}
            title="Fedina penale pulita"
            text="Non hai sanzioni amministrative né arresti registrati. Quando il server riceverà nuovi provvedimenti dal plugin custom, compariranno qui."
          />
        </div>
      ) : (
        <div className="mt-8 space-y-10">
          {showMulte && (
            <div data-testid="multe-block">
              <div className="flex items-center gap-2 mb-4">
                <Receipt className="w-4 h-4 text-[hsl(var(--brand-400))]" />
                <h4 className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--text-soft))]">
                  Multe ({multe.length})
                </h4>
              </div>
              {multe.length ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {multe.map((m, i) => <MultaCard key={i} multa={m} />)}
                </div>
              ) : (
                <EmptyCriminal
                  icon={Receipt}
                  title="Nessuna multa"
                  text="Non hai sanzioni amministrative registrate."
                />
              )}
            </div>
          )}

          {showArresti && (
            <div data-testid="arresti-block">
              <div className="flex items-center gap-2 mb-4">
                <Lock className="w-4 h-4 text-red-300" />
                <h4 className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--text-soft))]">
                  Arresti ({arresti.length})
                </h4>
              </div>
              {arresti.length ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {arresti.map((a, i) => <ArrestoCard key={i} arresto={a} />)}
                </div>
              ) : (
                <EmptyCriminal
                  icon={Lock}
                  title="Nessun arresto"
                  text="Non hai arresti registrati nella tua fedina penale."
                />
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
